import { NewCarFuelTypeFilterId } from "../../../../common-deprecated/constants";
import type {
    CarDataType,
    CompareMotorizationType,
    EquipmentSpecNameIdType,
    SpecItemType,
} from "../../../../common-deprecated/features/compare/utils/newCarCompareTypes";
import type { CommonSettingsType } from "../../../../common-deprecated/settings/fetchCommonSettings";
import { NcCompareContext } from "../../../../common-deprecated/types/NewCarCompareTypes";
import { formatSpecificationValue } from "../../../../common-deprecated/utils";
import type {
    EngineWithSpecsType,
    EquipmentAvailabilityType,
    EquipmentSpecItemType,
    PacksAvailabilityType,
    PacksNameIdType,
} from "../compareTypes";

const removeDuplicates = <T extends { [index: string]: any }>(myArr: T[], prop: string): T[] => {
    return myArr.filter((obj, pos, arr) => {
        return arr.map((mapObj) => mapObj[prop]).indexOf(obj[prop]) === pos;
    });
};
// We input al the titles of the specs and combine them into one list, remove the dupes and sort it.
export const parseTitlesOfSpecs = (rawTitles: SpecItemType[][]): SpecItemType[] => {
    // Turn into one big array
    const allResults: SpecItemType[] = ([] as SpecItemType[]).concat(...rawTitles);
    // Generate a SpecItemType result for each category
    const categories = [...new Set(allResults.map((x) => x.category))];
    return categories.map((category) => {
        const categoryResult = allResults
            .filter((x) => x.category === category)
            .reduce((acc: SpecItemType, res: SpecItemType) => {
                // Merge specs into 1 result
                return !acc ? res : { ...acc, specs: [...acc.specs, ...res.specs] };
            });
        // Remove duplicate specs & sort by index
        return {
            ...categoryResult,
            specs: removeDuplicates(categoryResult.specs, "id").sort((a, b) => a.index - b.index),
        };
    });
};

export const getSpecsForSelectedEngines = (
    rawSpecs: EngineWithSpecsType[][],
    selectedEngines: CompareMotorizationType[],
): { specs: { id: string; val: string }[]; fuelType: string }[] => {
    // Changed return type
    return rawSpecs.map((rawSpec, index) => {
        const matchedEngine = rawSpec.find(
            (x) =>
                x.engineId === selectedEngines[index].engineId &&
                x.transmissionId === selectedEngines[index].transmissionId &&
                x.wheeldriveId === selectedEngines[index].wheeldriveId,
        );

        return {
            specs: matchedEngine?.specs || [],
            fuelType: matchedEngine?.fuelTypeCode || "", // Include fuelType in the result
        };
    });
};

export const parseSpecs = (
    titlesOfSpecs: SpecItemType[],
    preParsedSpecs: { specs: { id: string; val: string }[]; fuelType: string }[], // Updated type
    commonSettings: CommonSettingsType,
): SpecItemType[] => {
    return titlesOfSpecs.map((cat) => {
        const parsedCat = { ...cat };
        parsedCat.specs = parsedCat.specs
            .map((spec) => {
                const newSpec = { ...spec };
                newSpec.values = preParsedSpecs.map((p) => {
                    const spe = p.specs.find((x) => x.id === newSpec.id);

                    const isElectric =
                        p.fuelType === NewCarFuelTypeFilterId.BatteryElectric ||
                        p.fuelType === NewCarFuelTypeFilterId.Electric;

                    return spe
                        ? {
                              value: formatSpecificationValue(spe.val, commonSettings.culture).trim(),
                              isElectric,
                          }
                        : {
                              value: "",
                              isElectric,
                          };
                });

                // returns true if every value in the array is the same as the first item in the array
                newSpec.allValuesAreTheSame = newSpec.values
                    .map((val) => val.value)
                    .every((val, i, arr) => val === arr[0]);
                return newSpec;
            })
            .filter((sp) => sp.values && !sp.values.map((val) => val.value).every((v) => v === ""));
        return parsedCat;
    });
};

// This does the same as parseTitlesOfSpecs except it has equipment in stead of specs and another type (plus root)
// When refactoring also make sure to swap out the function in the unit tests.
export const parseTitlesOfEquipment = (rawEquipment: EquipmentSpecItemType[][]): EquipmentSpecItemType[] => {
    // Turn into one big array
    const allResults: EquipmentSpecItemType[] = ([] as EquipmentSpecItemType[]).concat(...rawEquipment);
    // Generate a EquipmentSpecItemType result for each category
    const categories = [...new Set(allResults.map((x) => x.category))];
    return categories.map((category) => {
        const categoryResult = allResults
            .filter((x) => x.category === category)
            .reduce((acc: EquipmentSpecItemType, res: EquipmentSpecItemType) => {
                // Merge equipment into 1 result
                return !acc ? res : { ...acc, equipment: [...acc.equipment, ...res.equipment] };
            });
        // Remove duplicate equipment & sort by index
        return {
            ...categoryResult,
            equipment: removeDuplicates(categoryResult.equipment, "id").sort((a, b) => a.index - b.index),
        };
    });
};

export const parseTitlesOfPacks = (rawPacks: EquipmentSpecNameIdType[][]): EquipmentSpecNameIdType[] => {
    // Turn into one big array and remove duplicates
    return removeDuplicates(([] as EquipmentSpecNameIdType[]).concat(...rawPacks), "id");
};

export const parsePacks = (
    packs: PacksAvailabilityType[][],
    titles: PacksNameIdType[],
    carIds: string[],
): EquipmentSpecNameIdType[] => {
    return titles.map((title) => {
        return {
            name: title.name,
            id: title.id,
            code: "0",
            index: 0,
            values: packs.map((pack, index) => {
                const packItem = pack.find((p) => p.id === title.id);
                if (!packItem) return { value: "", equipment: [] };
                const engineAvailability = packItem?.cars?.find((car) => car.id === carIds[index]);
                return {
                    value: engineAvailability ? engineAvailability.equipped : packItem.equipped,
                    equipment: title.equipment || [],
                };
            }),
        };
    });
};

export const parsePacksLeasing = (grades: CarDataType[]): EquipmentSpecNameIdType[] => {
    // Get list of unique pack-names from all selected configurations
    const uniquePacks = [
        ...new Set(
            grades
                .map(({ configuration }) => configuration?.packs || [])
                .flatMap((array) => array.map((obj) => obj.name)),
        ),
    ];
    // Render configurations that include the pack as standard rather than optional
    return uniquePacks.map((packName) => {
        return {
            name: packName,
            id: packName,
            code: "0",
            index: 0,
            values: grades.map(({ configuration }) => ({
                value: configuration?.packs?.some((pack) => pack.name === packName) ? "s" : "",
                equipment: [],
            })),
        };
    });
};

export const checkEquipmentForPacks = (
    packs: PacksNameIdType[],
    item: EquipmentSpecNameIdType,
    index: number,
): PacksNameIdType[] => {
    const packsItems = packs
        .reduce<
            (PacksNameIdType | undefined)[]
        >((prevPack, pack) => [...prevPack, pack.values![index].value === "o" && pack.values![index].equipment?.find((equip) => equip.id === item.id) ? pack : undefined], [])
        .filter((pack) => pack);

    return packsItems as PacksNameIdType[];
};

export const parseEquipment = (
    ncCompareContext: NcCompareContext,
    equipment: EquipmentAvailabilityType[][],
    titles: EquipmentSpecItemType[],
    packs: EquipmentSpecNameIdType[],
    carIds: string[],
): EquipmentSpecItemType[] => {
    return titles.map((cat) => {
        const newCat = { ...cat };
        newCat.equipment = newCat.equipment
            .map((spec) => {
                const newSpec = { ...spec };

                newSpec.values = equipment.map((p, index) => {
                    let optionalEquipmentPrice;
                    const spe = p.find((x) => x.id === newSpec.id);
                    const engineAvailability = spe?.cars?.find((car) => car.id === carIds[index]);

                    const equipped = engineAvailability ? engineAvailability.equipped : spe ? spe.equipped : "";

                    const packItems = checkEquipmentForPacks(packs, newSpec, index);

                    // If the equipment is not equipped but is in an optional pack. Check for differnet pricing
                    if (!equipped && packItems && packItems.length > 0) {
                        const packEquipment = packItems.map((pack) =>
                            pack.values?.[index].equipment?.find(({ id }) => id === newSpec.id),
                        );
                        // Equipment can be in multiple packs at once. This takes the cheapest price to display as requested in OR-2867
                        optionalEquipmentPrice = packEquipment.reduce((reduceEquipment, prevEquipment) =>
                            (prevEquipment?.price?.list || 0) < (reduceEquipment?.price?.list || 0)
                                ? prevEquipment
                                : reduceEquipment,
                        );
                    }

                    return {
                        // New value for available in optional packs
                        value: packItems && packItems.length && !equipped ? "op" : equipped,
                        price: optionalEquipmentPrice ? optionalEquipmentPrice.price : spe && spe.price,
                        colours: spe ? spe.colours : [],
                    };
                });

                // Optional equipment are not shown in the Leasing compare
                if (ncCompareContext === NcCompareContext.Leasing) {
                    newSpec.values = newSpec.values.filter(({ value }) => !["op", "o"].includes(value as string));
                }

                // returns true if every value in the array is the same as the first item in the array
                newSpec.allValuesAreTheSame = newSpec.values
                    .map((val) => val.value)
                    .every((val, i, arr) => val === arr[0]);
                return newSpec;
            })
            .filter((eq) => eq.values && !eq.values.map((val) => val.value).every((v) => v === ""));

        return newCat;
    });
};
